import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-9d383894"),n=n(),_popScopeId(),n)
const _hoisted_1 = ["title", "aria-label"]
const _hoisted_2 = { class: "nav-search-dialog-input" }
const _hoisted_3 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", {
  class: "el-icon-search nav-search-icon",
  "aria-hidden": "true"
}, null, -1))
const _hoisted_4 = ["title"]
const _hoisted_5 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("span", { class: "nav-search-kbd-esc el-card" }, "Esc", -1))
const _hoisted_6 = [
  _hoisted_5
]
const _hoisted_7 = {
  key: 0,
  class: "nav-search-no-result"
}
const _hoisted_8 = ["onMouseenter", "onClick"]
const _hoisted_9 = ["innerHTML"]
const _hoisted_10 = ["innerHTML"]

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { MenuItem } from '@/router/nav-menu'
import { isEditableShortcutTarget, isPlainShiftChord } from '@/utils/keyboard-shortcut'
import KbdShiftLetter from '@/components/kbd-shift-letter/kbd-shift-letter.vue'

interface SearchResultItem {
  label: string
  path: string
  parentLabel?: string
}

export default /*#__PURE__*/_defineComponent({
  __name: 'nav-search',
  props: {
  menuList: {
    type: Array as () => MenuItem[],
    default: () => []
  }
},
  setup(__props) {

const props = __props
const router = useRouter()
const { t, locale } = useI18n()
const searchKeyword = ref('')
const dialogVisible = ref(false)
const activeIndex = ref(0)
const searchInputRef = ref()
const resultListRef = ref()

const searchKeywordTrimmed = computed(() => searchKeyword.value.trim())

function flattenMenu(menuList: MenuItem[], parentLabel?: string): SearchResultItem[] {
  const results: SearchResultItem[] = []
  menuList.forEach(item => {
    if (item.label) {
      const translatedLabel = t(item.label)
      if (item.path) {
        results.push({
          label: translatedLabel,
          path: item.path,
          parentLabel: parentLabel
        })
      }
      if (item.children && item.children.length > 0) {
        const currentParentLabel = parentLabel
          ? `${parentLabel} > ${translatedLabel}`
          : translatedLabel
        results.push(...flattenMenu(item.children, currentParentLabel))
      }
    }
  })
  return results
}

const allMenuItems = computed(() => {
  void locale.value
  return flattenMenu(props.menuList)
})

function highlightText(text: string, keyword: string): string {
  if (!keyword || !text) {
    return text
  }
  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  const regex = new RegExp(`(${escapedKeyword})`, 'gi')
  return text.replace(regex, '<mark class="nav-search-match">$1</mark>')
}

function matchScore(item: SearchResultItem, keyword: string): number {
  const l = item.label.toLowerCase()
  const p = item.parentLabel?.toLowerCase() ?? ''
  if (l.startsWith(keyword)) {
    return l.length
  }
  if (l.includes(keyword)) {
    return 1000 + l.length
  }
  if (p.includes(keyword)) {
    return 2000 + l.length
  }
  return 9000 + l.length
}

const filteredResults = computed<SearchResultItem[]>(() => {
  void locale.value
  const q = searchKeywordTrimmed.value.toLowerCase()
  const list = allMenuItems.value
  if (!q) {
    return list
  }
  const keyword = q
  const results = list.filter(item => {
    return item.label.toLowerCase().includes(keyword) ||
      (item.parentLabel && item.parentLabel.toLowerCase().includes(keyword))
  })
  results.sort((a, b) => matchScore(a, keyword) - matchScore(b, keyword))
  return results
})

// 关键词变化时重置高亮项
watch(searchKeyword, () => {
  activeIndex.value = 0
})

function scrollActiveIntoView() {
  nextTick(() => {
    const listEl = resultListRef.value as HTMLElement | undefined
    const activeEl = listEl?.querySelector('.nav-search-result-item.is-active') as HTMLElement | undefined
    activeEl?.scrollIntoView({ block: 'nearest' })
  })
}

function moveActive(step: number) {
  const len = filteredResults.value.length
  if (len === 0) return
  activeIndex.value = (activeIndex.value + step + len) % len
  scrollActiveIntoView()
}

function handleSelect(item: SearchResultItem | undefined) {
  if (!item || !item.path) return
  router.push(item.path)
  closeDialog()
}

function openDialog() {
  dialogVisible.value = true
}

function closeDialog() {
  dialogVisible.value = false
}

function handleDialogOpened() {
  searchInputRef.value?.focus?.()
}

function handleDialogClosed() {
  searchKeyword.value = ''
  activeIndex.value = 0
}

function handleInputKeydown(e: KeyboardEvent) {
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    moveActive(1)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    moveActive(-1)
  } else if (e.key === 'Enter') {
    handleSelect(filteredResults.value[activeIndex.value])
  } else if (e.key === 'Escape') {
    closeDialog()
  }
}

function handleGlobalKeydown(e: KeyboardEvent) {
  if (!isPlainShiftChord(e)) return
  if (e.key !== 'f' && e.key !== 'F') return

  const target = e.target as HTMLElement | null
  if (target?.closest('.nav-search')) {
    return
  }
  if (isEditableShortcutTarget(target)) {
    return
  }

  e.preventDefault()
  openDialog()
}

onMounted(() => {
  window.addEventListener('keydown', handleGlobalKeydown, true)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleGlobalKeydown, true)
})

return (_ctx: any,_cache: any) => {
  const _component_CmSvgIcon = _resolveComponent("CmSvgIcon")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_dialog = _resolveComponent("el-dialog")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("button", {
      title: _ctx.$t('nav.searchShortcutTitle'),
      class: "nav-search-icon-btn",
      "aria-label": _ctx.$t('nav.searchPlaceholder'),
      onClick: openDialog
    }, [
      _createVNode(_component_CmSvgIcon, {
        name: "assets/icons/search",
        class: "nav-search-svg",
        monochrome: "",
        size: 24
      }),
      _createVNode(KbdShiftLetter, { letter: "F" })
    ], 8, _hoisted_1),
    _createVNode(_component_el_dialog, {
      modelValue: dialogVisible.value,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dialogVisible).value = $event)),
      title: _ctx.$t('nav.searchDialogTitle'),
      width: "560px",
      top: "10vh",
      "append-to-body": "",
      "destroy-on-close": "",
      "custom-class": "nav-search-dialog",
      onOpened: handleDialogOpened,
      onClosed: handleDialogClosed
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_el_input, {
            ref_key: "searchInputRef",
            ref: searchInputRef,
            modelValue: searchKeyword.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchKeyword).value = $event)),
            placeholder: _ctx.$t('nav.searchPlaceholder'),
            onKeydown: handleInputKeydown
          }, {
            prefix: _withCtx(() => [
              _hoisted_3
            ]),
            _: 1
          }, 8, ["modelValue", "placeholder"]),
          _createElementVNode("div", {
            class: "nav-search-kbd-hint",
            title: _ctx.$t('nav.searchShortcutTitle')
          }, _hoisted_6, 8, _hoisted_4)
        ]),
        _createElementVNode("div", {
          ref_key: "resultListRef",
          ref: resultListRef,
          class: "nav-search-result-list"
        }, [
          (filteredResults.value.length === 0)
            ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(_ctx.$t('nav.searchNoResult')), 1))
            : (_openBlock(true), _createElementBlock(_Fragment, { key: 1 }, _renderList(filteredResults.value, (item, index) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: index,
                  class: _normalizeClass(["nav-search-result-item", { 'is-active': index === activeIndex.value }]),
                  onMouseenter: ($event: any) => (activeIndex.value = index),
                  onClick: ($event: any) => (handleSelect(item))
                }, [
                  _createElementVNode("div", {
                    class: "nav-search-result-label",
                    innerHTML: highlightText(item.label, searchKeywordTrimmed.value)
                  }, null, 8, _hoisted_9),
                  (item.parentLabel)
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 0,
                        class: "nav-search-result-path",
                        innerHTML: highlightText(item.parentLabel, searchKeywordTrimmed.value)
                      }, null, 8, _hoisted_10))
                    : _createCommentVNode("", true)
                ], 42, _hoisted_8))
              }), 128))
        ], 512)
      ]),
      _: 1
    }, 8, ["modelValue", "title"])
  ], 64))
}
}

})